/**
 * Day-level export column defaults for processing settings UI.
 * Mirrors processing/Configuration/day_export_registry.py groups.
 */

const TAC_REGION_SUFFIXES = [
  'mean', 'max', 'above_threshold_percent', 'below_threshold_percent',
  'auc', 'rise_rate_point_to_point', 'fall_rate_point_to_point',
  'ascending_duration', 'descending_duration'
]

const SLOT_CURVE_SUFFIXES = [
  'duration_CURVE', 'peak_CURVE', 'auc_total_CURVE', 'curve_threshold',
  'rise_rate_CURVE', 'fall_rate_CURVE', 'rise_rate_point_to_point_CURVE',
  'fall_rate_point_to_point_CURVE', 'rise_duration_CURVE', 'fall_duration_CURVE',
  'below_threshold_percent_CURVE', 'started_curve_count_CURVE',
  'device_worn_percent_CURVE', 'total_low_quality_percent_CURVE',
  'unimputed_low_quality_percent_CURVE', 'total_gap_percent_CURVE',
  'total_non_wear_percent_CURVE', 'total_jump_percent_CURVE',
  'total_plummet_percent_CURVE', 'total_extreme_negative_percent_CURVE',
  'low_quality_imputation_ratio_CURVE',
  'device_worn_percent_PERIPHERY_BEFORE', 'total_low_quality_percent_PERIPHERY_BEFORE',
  'unimputed_low_quality_percent_PERIPHERY_BEFORE', 'total_gap_percent_PERIPHERY_BEFORE',
  'total_non_wear_percent_PERIPHERY_BEFORE', 'total_extreme_negative_percent_PERIPHERY_BEFORE',
  'low_quality_imputation_ratio_PERIPHERY_BEFORE',
  'device_worn_percent_PERIPHERY_AFTER', 'total_low_quality_percent_PERIPHERY_AFTER',
  'unimputed_low_quality_percent_PERIPHERY_AFTER', 'total_gap_percent_PERIPHERY_AFTER',
  'total_non_wear_percent_PERIPHERY_AFTER', 'total_extreme_negative_percent_PERIPHERY_AFTER',
  'low_quality_imputation_ratio_PERIPHERY_AFTER',
  'start_offset_hours', 'end_offset_hours'
]

function tacRegionColumns(region) {
  return TAC_REGION_SUFFIXES.map((sfx) => `tac_${region}_${sfx}`)
}

function curveSlotColumns() {
  const cols = []
  for (let n = 1; n <= 3; n += 1) {
    for (const sfx of SLOT_CURVE_SUFFIXES) {
      cols.push(`curve_${n}_${sfx}`)
    }
  }
  return cols
}

const MEDIAN_PRIOR_COLUMNS = [
  'median_prior_peak_CURVE', 'median_prior_auc_total_CURVE', 'median_prior_duration_CURVE',
  'median_prior_total_low_quality_percent_CURVE', 'median_prior_rise_rate_CURVE',
  'median_prior_fall_rate_CURVE', 'median_prior_rise_rate_point_to_point_CURVE',
  'median_prior_fall_rate_point_to_point_CURVE', 'median_prior_fall_duration_CURVE',
  'median_prior_rise_duration_CURVE', 'median_prior_tac_q1_mean', 'median_prior_tac_q1_max',
  'median_prior_tac_q1_auc', 'median_prior_tac_q2_q4_mean', 'median_prior_tac_q2_q4_max',
  'median_prior_tac_q2_q4_auc', 'median_prior_curve_1_start_offset_hours',
  'median_prior_curve_1_end_offset_hours'
]

const OVERLAP_DIAGNOSTIC = [
  'drinking_curve_overlap', 'predicted_drinking_curve_overlap',
  'predicted_drinking_day_by_curve_start', 'invalid_region_curve_overlap',
  'total_curve_overlap_hours', 'predicted_drinking_overlap_hours'
]

import { RELIABILITY_FLAG_COLUMNS } from './drinkingPredictionReliability.js'

export const PREDICTION_COLUMNS = ['pred_drinking_day', 'proba_drinking_day', ...RELIABILITY_FLAG_COLUMNS]

/** Subsections under Standard Day Metrics (default on unless noted). */
export const STANDARD_DAY_METRIC_SECTIONS = [
  {
    id: 'identity',
    label: 'Day & device identity',
    defaultExport: true,
    columns: [
      'SubID', 'Dataset_ID', 'day_no', 'begin_day', 'end_day', 'date',
      'device_ids', 'device_one', 'device_two', 'device_count', 'firmware', 'day_hours'
    ]
  },
  {
    id: 'device_change',
    label: 'Device change',
    defaultExport: false,
    columns: ['new_device']
  },
  {
    id: 'wear',
    label: 'Device wear & imputation',
    defaultExport: true,
    columns: [
      'device_turned_on_duration', 'device_turned_on_percent_of_day',
      'device_worn_duration', 'device_worn_percent_of_device_on', 'device_worn_percent_of_day',
      'device_worn_duration_cutoff', 'device_worn_cutoff_percent_of_device_on', 'device_worn_cutoff_percent_of_day',
      'imputed_duration', 'imputed_percent_of_day', 'imputed_percent_of_device_on'
    ]
  },
  {
    id: 'lq_overall',
    label: 'Low quality — overall',
    defaultExport: true,
    columns: [
      'low_quality_duration', 'low_quality_percent_of_day', 'low_quality_percent_of_device_on',
      'unimputed_low_quality_duration', 'unimputed_low_quality_percent_of_day', 'unimputed_low_quality_percent_of_device_on',
      'imputed_low_quality_duration', 'imputed_low_quality_percent_of_day', 'imputed_low_quality_percent_of_device_on',
      'low_quality_imputation_ratio'
    ]
  },
  {
    id: 'lq_model',
    label: 'Low quality — drinking-day summary',
    defaultExport: false,
    columns: ['low_quality_percent']
  },
  {
    id: 'lq_gap',
    label: 'Low quality — gaps',
    defaultExport: true,
    columns: [
      'gap_duration', 'gap_percent_of_day', 'gap_percent_of_device_on',
      'imputed_gap_duration', 'imputed_gap_percent_of_day', 'imputed_gap_percent_of_device_on',
      'unimputed_gap_duration', 'unimputed_gap_percent_of_day', 'unimputed_gap_percent_of_device_on',
      'gap_imputation_ratio'
    ]
  },
  {
    id: 'lq_non_wear',
    label: 'Low quality — non-wear',
    defaultExport: true,
    columns: [
      'non_wear_duration', 'non_wear_percent_of_day', 'non_wear_percent_of_device_on',
      'imputed_non_wear_duration', 'imputed_non_wear_percent_of_day', 'imputed_non_wear_percent_of_device_on',
      'unimputed_non_wear_duration', 'unimputed_non_wear_percent_of_day', 'unimputed_non_wear_percent_of_device_on',
      'non_wear_imputation_ratio'
    ]
  },
  {
    id: 'lq_jump',
    label: 'Low quality — jumps',
    defaultExport: true,
    columns: [
      'jump_duration', 'jump_percent_of_day', 'jump_percent_of_device_on',
      'imputed_jump_duration', 'imputed_jump_percent_of_day', 'imputed_jump_percent_of_device_on',
      'unimputed_jump_duration', 'unimputed_jump_percent_of_day', 'unimputed_jump_percent_of_device_on',
      'jump_imputation_ratio'
    ]
  },
  {
    id: 'lq_plummet',
    label: 'Low quality — plummets',
    defaultExport: true,
    columns: [
      'plummet_duration', 'plummet_percent_of_day', 'plummet_percent_of_device_on',
      'imputed_plummet_duration', 'imputed_plummet_percent_of_day', 'imputed_plummet_percent_of_device_on',
      'unimputed_plummet_duration', 'unimputed_plummet_percent_of_day', 'unimputed_plummet_percent_of_device_on',
      'plummet_imputation_ratio'
    ]
  },
  {
    id: 'lq_negative',
    label: 'Low quality — negative readings',
    defaultExport: true,
    columns: [
      'negative_duration', 'negative_percent_of_day', 'negative_percent_of_device_on',
      'extreme_negative_duration', 'extreme_negative_percent_of_day', 'extreme_negative_percent_of_device_on',
      'extreme_negative_sum',
      'imputed_extreme_negative_duration', 'imputed_extreme_negative_percent_of_day', 'imputed_extreme_negative_percent_of_device_on',
      'unimputed_extreme_negative_duration', 'unimputed_extreme_negative_percent_of_day', 'unimputed_extreme_negative_percent_of_device_on',
      'extreme_negative_imputation_ratio'
    ]
  },
  {
    id: 'lq_combined',
    label: 'Low quality — combined summary',
    defaultExport: true,
    columns: ['total_gaps_and_non_wear_percent']
  },
  {
    id: 'tac_day',
    label: 'TAC — day summary',
    defaultExport: true,
    columns: ['below_threshold_percent', 'flatline_max', 'flatlined_percent']
  },
  {
    id: 'tac_model',
    label: 'TAC — drinking-day summary',
    defaultExport: false,
    columns: ['above_threshold_high_quality_percent_of_day']
  },
  {
    id: 'tac_q1',
    label: 'TAC — morning window (6:00 AM–12:00 PM)',
    defaultExport: false,
    columns: tacRegionColumns('q1')
  },
  {
    id: 'tac_q2_q4',
    label: 'TAC — afternoon/evening window (12:00 PM–6:00 AM)',
    defaultExport: false,
    columns: tacRegionColumns('q2_q4')
  },
  {
    id: 'temp_motion',
    label: 'Temperature & motion',
    defaultExport: true,
    columns: [
      'temp_mean', 'temp_sd', 'temp_min', 'temp_max',
      'motion_mean', 'motion_sd', 'motion_min', 'motion_max'
    ]
  }
]

/** Typical skynDay columns (default on); excludes optional ML algorithm columns. */
export const CORE_DAY_EXPORT_COLUMNS = STANDARD_DAY_METRIC_SECTIONS
  .filter((s) => s.defaultExport)
  .flatMap((s) => s.columns)

/** Required for Daily Tracking (Adherence Report); always exported and locked in UI. */
export const DAILY_TRACKING_REQUIRED_COLUMNS = [...CORE_DAY_EXPORT_COLUMNS]

export function isLockedExportColumn(col, algorithmEnabled = false) {
  if (DAILY_TRACKING_REQUIRED_COLUMNS.includes(col)) {
    return true
  }
  if (algorithmEnabled && PREDICTION_COLUMNS.includes(col)) {
    return true
  }
  return false
}

function enforceLockedExportColumns(map, algorithmEnabled = false) {
  for (const col of DAILY_TRACKING_REQUIRED_COLUMNS) {
    if (Object.prototype.hasOwnProperty.call(map, col)) {
      map[col] = true
    }
  }
  if (algorithmEnabled) {
    for (const col of PREDICTION_COLUMNS) {
      if (Object.prototype.hasOwnProperty.call(map, col)) {
        map[col] = true
      }
    }
  }
  return map
}

export function columnsInExportGroup(group) {
  if (group.sections) {
    return group.sections.flatMap((s) => s.columns)
  }
  return group.columns || []
}

export const DAY_EXPORT_FEATURE_GROUPS = [
  {
    id: 'standard',
    label: 'Standard Day Metrics',
    sections: STANDARD_DAY_METRIC_SECTIONS
  },
  { id: 'curve_slot', label: 'Curve overlap slots 1–3', columns: curveSlotColumns(), defaultExport: false },
  { id: 'median_prior', label: 'Prior-day medians', columns: MEDIAN_PRIOR_COLUMNS, defaultExport: false },
  { id: 'overlap_diag', label: 'Curve overlap diagnostics', columns: OVERLAP_DIAGNOSTIC, defaultExport: false },
  { id: 'prediction', label: 'Drinking-day predictions', columns: PREDICTION_COLUMNS, defaultExport: true, lockedWhenAlgorithm: true }
]

export function defaultExportForColumn(col) {
  for (const group of DAY_EXPORT_FEATURE_GROUPS) {
    if (group.sections) {
      for (const section of group.sections) {
        if (section.columns.includes(col)) {
          return section.defaultExport
        }
      }
    } else if (group.columns?.includes(col)) {
      if (group.id === 'prediction') {
        return false
      }
      return group.defaultExport
    }
  }
  return true
}

export function buildDefaultDayExportColumns(algorithmEnabled = false) {
  const map = {}
  for (const group of DAY_EXPORT_FEATURE_GROUPS) {
    if (group.sections) {
      for (const section of group.sections) {
        for (const col of section.columns) {
          map[col] = section.defaultExport
        }
      }
    } else {
      for (const col of group.columns) {
        map[col] = group.defaultExport
      }
    }
  }
  return enforceLockedExportColumns(map, algorithmEnabled)
}

export function mergeDayExportColumns(existing = {}, algorithmEnabled = false) {
  const defaults = buildDefaultDayExportColumns(algorithmEnabled)
  const merged = { ...defaults, ...existing }
  return enforceLockedExportColumns(merged, algorithmEnabled)
}

/** Minimal column set for quick review. */
export const ESSENTIAL_COLUMNS = [
  'SubID', 'Dataset_ID', 'day_no', 'date', 'begin_day', 'end_day',
  'day_hours', 'device_turned_on_percent_of_day',
  'device_worn_percent_of_day', 'low_quality_percent_of_day',
  'total_gaps_and_non_wear_percent',
  'temp_mean', 'motion_mean'
]

/** Drinking-day prediction columns included in the Essential preset. */
export const ESSENTIAL_PREDICTION_COLUMNS = ['pred_drinking_day', 'proba_drinking_day']

export function buildEssentialDayExportColumns(algorithmEnabled = false) {
  const map = {}
  const essentialSet = new Set([...ESSENTIAL_COLUMNS, ...ESSENTIAL_PREDICTION_COLUMNS])
  for (const group of DAY_EXPORT_FEATURE_GROUPS) {
    for (const col of columnsInExportGroup(group)) {
      map[col] = essentialSet.has(col)
    }
  }
  return enforceLockedExportColumns(map, algorithmEnabled)
}

export function buildAllDayExportColumns() {
  const map = {}
  for (const group of DAY_EXPORT_FEATURE_GROUPS) {
    for (const col of columnsInExportGroup(group)) {
      map[col] = true
    }
  }
  return map
}
